import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import { useLocation, useNavigate } from 'react-router-dom';

import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import PageLoader from '../components/ui/PageLoader';
import { CrmBadge, CrmButton, CrmDateInput, CrmInput, CrmModal, CrmSearchInput, CrmSelect, CrmTimeInput, CrmTooltip } from '../components/ui';
import CrmListPage from '../components/listing/CrmListPage';
import CrmColumnManager from '../components/listing/CrmColumnManager';
import { fetchKots, updateKotStatus } from '../services/kotService';
import { useNotification } from '../context/NotificationContext';
import api from '../services/api';
import { downloadPdfFromResponse, printPdfFromResponse } from '../utils/pdfHelpers';
import { parseDateInput, toInputDateValue, formatDate as formatLocalDate } from '../utils/dateFormatter';
import { formatStatusLabel } from '../utils/statusFormatter';
import useAutoRefresh from '../hooks/useAutoRefresh';
import '../assets/styles/OperationalBoard.scss';

const STATUS_OPTIONS = ['pending', 'preparing', 'ready', 'completed', 'cancelled'];
const RANGE_OPTIONS = ['today', 'tomorrow', 'upcoming', 'all'];
const CARD_PREF_KEY = 'jdh:crm:kots:card-properties';

const KOT_CARD_FIELDS = [
  { id: 'customer', label: 'Customer', hideable: false },
  { id: 'source', label: 'Order source' },
  { id: 'box_type', label: 'Box type' },
  { id: 'status', label: 'Order status', hideable: false },
  { id: 'event', label: 'Event' },
  { id: 'order_date', label: 'Order placed', hideable: false },
  { id: 'schedule', label: 'Event / delivery schedule', hideable: false },
  { id: 'address', label: 'Address' },
  { id: 'pax_payment', label: 'PAX / payment' },
  { id: 'notes', label: 'Notes', defaultHidden: true },
  { id: 'items', label: 'Kitchen items', hideable: false },
];

const defaultCardPreferences = () => ({
  order: KOT_CARD_FIELDS.map((field) => field.id),
  hidden: KOT_CARD_FIELDS.filter((field) => field.defaultHidden).map((field) => field.id),
  pinned: null,
  widths: {},
});

const readCardPreferences = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(CARD_PREF_KEY) || 'null');
    if (!saved || typeof saved !== 'object') return defaultCardPreferences();
    const validIds = new Set(KOT_CARD_FIELDS.map((field) => field.id));
    const order = Array.isArray(saved.order) ? saved.order.filter((id) => validIds.has(id)) : [];
    KOT_CARD_FIELDS.forEach((field) => { if (!order.includes(field.id)) order.push(field.id); });
    return {
      order,
      hidden: Array.isArray(saved.hidden) ? saved.hidden.filter((id) => validIds.has(id) && KOT_CARD_FIELDS.find((field) => field.id === id)?.hideable !== false) : [],
      pinned: null,
      widths: {},
    };
  } catch {
    return defaultCardPreferences();
  }
};

const formatNumberTrimZeros = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return Number.isInteger(num) ? String(num) : String(num);
};

const plainText = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim();
const formatQty = (qty) => `${formatNumberTrimZeros(qty || 0)} Qty`;

const getBundledItems = (item = {}) => {
  const candidates = [item.bundle_items, item.bundleItems, item.components, item.component_products];
  return candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) || [];
};

const getComponentQuantity = (component = {}) => {
  const quantity = Number(component.quantity ?? component.qty ?? component.component_quantity ?? 1);
  return Number.isFinite(quantity) ? quantity : 1;
};

const getOrderedQuantity = (item = {}) => {
  const quantity = Number(item.quantity ?? 0);
  return Number.isFinite(quantity) ? quantity : 0;
};

const getKitchenItemCount = (items = []) => items.reduce((count, item) => count + (getBundledItems(item).length || 1), 0);
const formatEventDate = (dateValue) => dateValue ? (formatLocalDate(dateValue) || String(dateValue)) : '—';

const formatEventTime = (timeValue) => {
  if (!timeValue) return '—';
  const raw = String(timeValue).trim();
  const match = raw.match(/^(\d{2}:\d{2})(?::\d{2})?$/);
  if (!match) return raw;
  const parsed = parseDateInput(`1970-01-01T${match[1]}:00`);
  return parsed ? parsed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : raw;
};

const isWebsiteKot = (kot) => {
  const event = kot?.event_snapshot || {};
  return String(kot?.source_type || '').toUpperCase() === 'WEBSITE'
    || String(kot?.mode || '').toUpperCase() === 'WEBSITE'
    || String(event?.name || event?.event_name || kot?.event_name || '').trim().toLowerCase() === 'website order';
};

const normalizeBoxType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'jain') return 'Jain';
  if (normalized === 'swaminarayan') return 'Swaminarayan';
  if (normalized === 'standard') return 'Standard';
  return '';
};

const getKotBoxType = (kot) => {
  if ((kot.items || []).some((item) => /^Box \d+:/m.test(String(item.product_description || '')))) return 'Per-box preferences';
  const event = kot?.event_snapshot || {};
  const explicit = normalizeBoxType(kot?.box_type || event?.box_type || event?.booking_type);
  if (explicit) return explicit;
  const notesMatch = String(kot?.order_notes || '').match(/Box type:\s*([^\r\n]+)/i);
  return normalizeBoxType(notesMatch?.[1]) || (isWebsiteKot(kot) ? 'Standard' : '');
};

const formatSlotHour = (date) => date.toLocaleTimeString('en-IN', { hour: 'numeric', ...(date.getMinutes() ? { minute: '2-digit' } : {}), hour12: true });
const formatDeliverySlot = (timeValue) => {
  const match = String(timeValue || '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return formatEventTime(timeValue);
  const start = new Date(1970, 0, 1, Number(match[1]), Number(match[2]), 0, 0);
  return `${formatSlotHour(start)} - ${formatSlotHour(new Date(start.getTime() + 3600000))}`;
};

const getScheduleLabel = (kot) => {
  const event = kot?.event_snapshot || {};
  const date = kot?.effective_schedule_date || event.date || event.event_date || kot?.event_date;
  const time = event.time || event.event_time || kot?.event_time;
  const formattedDate = formatEventDate(date);
  const formattedTime = isWebsiteKot(kot) ? formatDeliverySlot(time) : formatEventTime(time);
  return [formattedDate, formattedTime].filter((value) => value && value !== '—').join(' · ') || 'Schedule not recorded';
};

const Fact = ({ label, children, wide = false }) => (
  <div className={`operations-card__fact ${wide ? 'operations-card__fact--wide' : ''}`}>
    <span>{label}</span><strong title={typeof children === 'string' ? children : undefined}>{children || '—'}</strong>
  </div>
);

function KOTBoard() {
  const { getUnreadNotificationFor, markRecordNotificationsSeen } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [range, setRange] = useState('upcoming');
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const [displayView, setDisplayView] = useState(() => { try { return localStorage.getItem('jdh:kots:display') === 'kds' ? 'kds' : 'cards'; } catch { return 'cards'; } });
  const [dragTarget, setDragTarget] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [busyIds, setBusyIds] = useState(new Set());
  const busyRef = useRef(new Set());
  const requestRef = useRef(0);
  const [kots, setKots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState({ open: false, message: '', severity: 'success' });
  const [newKotIds, setNewKotIds] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('scheduled');
  const [cardManagerOpen, setCardManagerOpen] = useState(false);
  const [cardPreferences, setCardPreferences] = useState(readCardPreferences);
  const [completionDialog, setCompletionDialog] = useState({ open: false, kot: null, loading: false, workflow: null, deliveryDate: '', deliveryTime: '' });
  const [completing, setCompleting] = useState(false);
  const knownKotIdsRef = useRef(null);

  const showNotification = (message, severity = 'success') => setNotif({ open: true, message, severity });

  const loadKots = async (selectedRange = range, { isAutoRefresh = false } = {}) => {
    if (selectedRange !== rangeRef.current) return;
    const request = ++requestRef.current;
    try {
      if (!isAutoRefresh) setLoading(true);
      setLoadError('');
      const res = await fetchKots(selectedRange);
      if (request !== requestRef.current) return;
      const nextKots = res?.kots || [];
      const nextIds = new Set(nextKots.map((kot) => String(kot.id)));
      if (knownKotIdsRef.current) {
        const additions = [...nextIds].filter((id) => !knownKotIdsRef.current.has(id));
        if (additions.length) setNewKotIds((current) => new Set([...current, ...additions]));
      }
      knownKotIdsRef.current = nextIds;
      setKots(nextKots);
    } catch (error) {
      if (request === requestRef.current) setLoadError(error?.response?.data?.error || 'Failed to fetch KOT list');
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  };

  useAutoRefresh((context) => context.isAutoRefresh ? loadKots(range, context) : Promise.resolve(), { enabled: !loading && !completionDialog.open && !busyIds.size && !dragTarget, intervalMs: 15000 });
  useEffect(() => { loadKots(range); return () => { requestRef.current += 1; }; }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const rangeQuery = String(params.get('range') || '').trim().toLowerCase();
    if (RANGE_OPTIONS.includes(rangeQuery)) setRange(rangeQuery);
    const focusQuery = String(params.get('focusOrderNo') || params.get('focusKotId') || params.get('notification_source_id') || '').trim();
    if (!focusQuery) return;
    setRange('all'); setStatusFilter('all'); setSearchQuery(focusQuery);
    params.delete('focusOrderNo'); params.delete('focusKotId'); params.delete('notification_source_id');
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const filteredAndSortedKots = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = [...kots].filter((kot) => {
      const event = kot.event_snapshot || {};
      if (statusFilter !== 'all' && kot.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && (isWebsiteKot(kot) ? 'website' : 'catering') !== sourceFilter) return false;
      if (!q) return true;
      return [kot.id, kot.order_number, kot.customer_name, event.name, event.address, event.location, event.venue, kot.event_name]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
    return filtered.sort((a, b) => {
      const eventA = a.event_snapshot || {}; const eventB = b.event_snapshot || {};
      if (sortBy === 'status') return (STATUS_OPTIONS.indexOf(a.status) < 0 ? 999 : STATUS_OPTIONS.indexOf(a.status)) - (STATUS_OPTIONS.indexOf(b.status) < 0 ? 999 : STATUS_OPTIONS.indexOf(b.status));
      if (sortBy === 'pax') return Number(eventB.pax || b.pax || 0) - Number(eventA.pax || a.pax || 0);
      const dateA = parseDateInput((a.effective_schedule_date || eventA.date) ? `${a.effective_schedule_date || eventA.date}T${eventA.time || '00:00:00'}` : a.scheduled_for || a.scheduledAt || (a.event_date ? `${a.event_date}T${a.event_time || '00:00:00'}` : 0)) || new Date(0);
      const dateB = parseDateInput((b.effective_schedule_date || eventB.date) ? `${b.effective_schedule_date || eventB.date}T${eventB.time || '00:00:00'}` : b.scheduled_for || b.scheduledAt || (b.event_date ? `${b.event_date}T${b.event_time || '00:00:00'}` : 0)) || new Date(0);
      return dateA - dateB;
    });
  }, [kots, searchQuery, sortBy, sourceFilter, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: kots.length };
    STATUS_OPTIONS.forEach((status) => { counts[status] = 0; });
    kots.forEach((kot) => { counts[kot.status] = (counts[kot.status] || 0) + 1; });
    return counts;
  }, [kots]);

  const visibleFieldIds = useMemo(() => cardPreferences.order.filter((id) => !cardPreferences.hidden.includes(id)), [cardPreferences]);
  const visible = (id) => visibleFieldIds.includes(id);
  const hasActiveFilters = Boolean(searchQuery || range !== 'upcoming' || statusFilter !== 'all' || sourceFilter !== 'all' || sortBy !== 'scheduled');

  const applyCardPreferences = (next) => {
    setCardPreferences(next);
    try { localStorage.setItem(CARD_PREF_KEY, JSON.stringify(next)); } catch { /* current session still works */ }
  };

  const nextKotAction = (kot) => {
    const status = String(kot?.status || '').toLowerCase();
    if (status === 'pending') return { status: 'preparing', label: 'Start Preparing' };
    if (status === 'preparing') return { status: 'ready', label: 'Mark Ready' };
    if (status === 'ready') return { status: 'completed', label: 'Complete / Handoff' };
    return null;
  };

  const handleStatusChange = async (kot, status) => {
    if (busyRef.current.has(kot.id) || nextKotAction(kot)?.status !== status) return;
    busyRef.current.add(kot.id); setBusyIds(new Set(busyRef.current));
    try {
      await updateKotStatus(kot.id, status);
      showNotification(status === 'completed' ? 'KOT completed and fulfilment workflow updated' : 'KOT status updated');
      await loadKots(rangeRef.current);
    } catch (error) { showNotification(error?.response?.data?.error || 'Failed to update KOT status', 'error'); }
    finally { busyRef.current.delete(kot.id); setBusyIds(new Set(busyRef.current)); }
  };

  const openCompletionDialog = async (kot) => {
    const event = kot?.event_snapshot || {};
    const deliveryDate = toInputDateValue(event.date || event.event_date || kot?.event_date || new Date());
    const rawTime = String(event.time || event.event_time || kot?.event_time || '').match(/^(\d{2}:\d{2})/);
    const deliveryTime = rawTime ? rawTime[1] : '';
    if (busyRef.current.has(kot.id)) return;
    setCompletionDialog({ open: true, kot, loading: true, workflow: null, deliveryDate: kot.delivery_date ? toInputDateValue(kot.delivery_date) : deliveryDate, deliveryTime: String(kot.delivery_time || deliveryTime).slice(0,5), driverName: kot.delivery_man_name || '', driverPhone: kot.delivery_man_phone || '', driverVehicle: kot.delivery_man_vehicle || '', error: '' });
    let workflow = null;
    if (kot.order_id) {
      try { workflow = (await api.get(`/orders/${kot.order_id}`))?.data || null; } catch {
        setCompletionDialog((current) => current.open && current.kot?.id === kot.id ? { ...current, loading: false, error: 'Unable to load fulfilment details. Close and retry.' } : current); return;
      }
    }
    setCompletionDialog((current) => current.open && current.kot?.id === kot.id ? { ...current, loading: false, workflow } : current);
  };

  const completionMode = String(completionDialog.workflow?.order?.fulfilment_mode || completionDialog.kot?.fulfilment_mode || completionDialog.workflow?.profile?.default_fulfilment_mode || '').toUpperCase();
  const completionValid = !completionDialog.error && !completionDialog.loading && (completionMode !== 'DELIVERY' || Boolean(completionDialog.deliveryDate && completionDialog.deliveryTime && completionDialog.driverName?.trim() && completionDialog.driverPhone?.trim()));
  const confirmComplete = async () => {
    if (!completionDialog.kot || completing || !completionValid || busyRef.current.has(completionDialog.kot.id)) return;
    busyRef.current.add(completionDialog.kot.id);
    try {
      setCompleting(true);
      await updateKotStatus(completionDialog.kot.id, { status: 'completed', delivery_date: completionDialog.deliveryDate || null, delivery_time: completionDialog.deliveryTime || null, delivery_man_name: completionDialog.driverName, delivery_man_phone: completionDialog.driverPhone, delivery_man_vehicle: completionDialog.driverVehicle });
      showNotification('KOT completed and fulfilment workflow updated');
      await loadKots(rangeRef.current);
      setCompletionDialog({ open: false, kot: null, loading: false, workflow: null, deliveryDate: '', deliveryTime: '' });
    } catch (error) { showNotification(error?.response?.data?.error || 'Handoff failed. Please retry.', 'error'); } finally { busyRef.current.delete(completionDialog.kot.id); setCompleting(false); }
  };


  const handlePrintKot = async (kotId) => {
    try { await printPdfFromResponse(await api.get(`/kots/${kotId}/pdf`, { responseType: 'blob' }), 'Failed to print KOT PDF'); }
    catch (error) { showNotification(error?.message || error?.response?.data?.error || 'Failed to print KOT', 'error'); }
  };

  const handleDownloadKot = async (kot) => {
    const safe = (value, fallback) => String(value || fallback || '').trim().replace(/[<>:"|?*]+/g, '-').replace(/[\/\\]+/g, '-').replace(/\s+/g, ' ').replace(/[. ]+$/g, '') || fallback;
    const customer = safe(kot?.customer_name, 'Customer');
    const number = safe(kot?.order_number || kot?.id, String(kot?.id || 'KOT'));
    try { await downloadPdfFromResponse(await api.get(`/kots/${kot.id}/pdf`, { responseType: 'blob' }), `Kitchen-Order-${customer}-${number}.pdf`, 'Failed to download KOT PDF'); }
    catch (error) { showNotification(error?.response?.data?.error || 'Failed to download KOT', 'error'); }
  };

  const handleOpenKotRecord = async (kotId) => {
    setNewKotIds((current) => { const next = new Set(current); next.delete(String(kotId)); return next; });
    try { await markRecordNotificationsSeen('kot', kotId); } catch { /* notification failure must not block KOT work */ }
  };

  const renderCardField = (fieldId, kot) => {
    const event = kot.event_snapshot || {};
    const website = isWebsiteKot(kot);
    const eventAddress = event.address || event.location || event.venue || kot.event_location || '—';
    if (fieldId === 'customer') return <Fact key={fieldId} label="Customer">{kot.customer_name || 'Customer'}</Fact>;
    if (fieldId === 'event' && !website) return <Fact key={fieldId} label="Event">{event.name || event.event_name || kot.event_name || '—'}</Fact>;
    if (fieldId === 'order_date') return <Fact key={fieldId} label="Order placed">{kot.order_placed_at ? formatEventDate(kot.order_placed_at) : 'Not recorded'}</Fact>;
    if (fieldId === 'schedule') return <Fact key={fieldId} label={website ? 'Delivery' : 'Event schedule'}>{getScheduleLabel(kot)}</Fact>;
    if (fieldId === 'address') return <Fact key={fieldId} label="Address" wide>{eventAddress}</Fact>;
    if (fieldId === 'pax_payment') return <Fact key={fieldId} label={website ? 'Payment' : 'PAX'}>{website ? formatStatusLabel(kot.payment_status || 'pending') : formatNumberTrimZeros(event.pax || kot.pax || '') || 'Not available'}</Fact>;
    if (fieldId === 'notes') {
      const notes = plainText(event.notes || event.event_notes || '');
      return notes ? <div key={fieldId} className="operations-card__note">{notes}</div> : null;
    }
    return null;
  };

  const renderKotCard = (kot, kotIndex = 0) => {
                  const website = isWebsiteKot(kot);
                  const boxType = getKotBoxType(kot);
                  const unread = getUnreadNotificationFor?.('kot', kot.id);
                  const isNew = newKotIds.has(String(kot.id)) || Boolean(unread && /(create|new|added)/i.test(String(unread.action || '')));
                  const statusTone = { pending: 'warning', preparing: 'info', ready: 'info', completed: 'success' }[kot.status] || 'neutral';
                  const bodyFieldIds = visibleFieldIds.filter((id) => !['source', 'box_type', 'status', 'items'].includes(id));
                  return (
                    <article key={`${kot.id}-${kotIndex}`} className={`operations-card ${isNew ? 'is-new' : ''}`}>
                      <header className="operations-card__top">
                        <div className="operations-card__identity">
                          <p className="operations-card__customer">KOT #{kot.id}</p>
                          <button type="button" className="operations-card__number" onClick={() => handleOpenKotRecord(kot.id)}>{kot.order_number || `Order #${kot.order_id || kot.id}`}</button>
                          <div className="operations-card__badges">
                            {isNew ? <CrmBadge tone="success" className="operations-card__badge--new">NEW</CrmBadge> : null}
                            {visible('source') ? <CrmBadge tone={website ? 'info' : 'success'} className={`operations-card__badge--${website ? 'website' : 'catering'}`}>{website ? 'Website order' : 'Catering order'}</CrmBadge> : null}
                            {visible('box_type') && boxType ? <CrmBadge tone="warning">{boxType}</CrmBadge> : null}
                          </div>
                        </div>
                        {visible('status') ? <div className="operations-card__status"><CrmBadge tone={statusTone}>{formatStatusLabel(kot.status)}</CrmBadge></div> : null}
                      </header>
                      <div className="operations-card__body">
                        <div className="operations-card__facts">{bodyFieldIds.map((fieldId) => renderCardField(fieldId, kot))}</div>
                        {visible('items') ? <>
                          <div className="operations-card__section-title"><span>Kitchen items</span><CrmBadge tone="neutral">{getKitchenItemCount(kot.items || [])}</CrmBadge></div>
                          <div className="operations-card__items">
                            {(kot.items || []).map((item, itemIndex) => {
                              const bundles = getBundledItems(item); const orderedQuantity = getOrderedQuantity(item);
                              return <div className="operations-card__item" key={`${item.id}-${itemIndex}`}>
                                <div className="operations-card__item-row"><strong>{item.product_name}</strong><strong>{formatQty(orderedQuantity)}</strong></div>
                                {item.product_description ? <div className="operations-card__item-detail">{plainText(item.product_description)}</div> : null}
                                {bundles.length ? <div className="operations-card__components">{bundles.map((component, index) => <div className="operations-card__component-row" key={component.id || component.component_product_id || component.product_id || index}><span>{component.name || component.product_name || component.component_name || component.product?.name || 'Item'}</span><strong>{formatNumberTrimZeros(getComponentQuantity(component) * orderedQuantity)} {component.unit || component.selling_unit || component.component_unit || 'Qty'}</strong></div>)}</div> : null}
                              </div>;
                            })}
                          </div>
                        </> : null}
                      </div>
                      <footer className="operations-card__footer operations-card__footer--kot">
                        <CrmButton size="small" variant="tertiary" startIcon={<PrintOutlinedIcon />} onClick={() => handlePrintKot(kot.id)}>Print</CrmButton>
                        <CrmButton size="small" variant="tertiary" startIcon={<DownloadOutlinedIcon />} onClick={() => handleDownloadKot(kot)}>Download</CrmButton>
                        {(() => {
                          const nextAction = nextKotAction(kot);
                          if (!nextAction) return null;
                          if (nextAction.status === 'completed') {
                            return <CrmButton disabled={busyIds.has(kot.id) || completionDialog.open} size="small" variant="primary" startIcon={<DoneOutlinedIcon />} onClick={() => openCompletionDialog(kot)}>{nextAction.label}</CrmButton>;
                          }
                          return <CrmButton disabled={busyIds.has(kot.id) || completionDialog.open} size="small" variant="primary" onClick={() => handleStatusChange(kot, nextAction.status)}>{nextAction.label}</CrmButton>;
                        })()}
                      </footer>
                    </article>
                  );
  };

  const onDragEnd = ({ draggableId, destination, source }) => {
    setDragTarget(null);
    if (!destination || destination.droppableId === source.droppableId) return;
    const kot = kots.find((ticket) => String(ticket.id) === draggableId);
    if (!kot || busyRef.current.has(kot.id) || nextKotAction(kot)?.status !== destination.droppableId) return;
    if (destination.droppableId === 'completed') openCompletionDialog(kot);
    else handleStatusChange(kot, destination.droppableId);
  };

  if (loading && !kots.length) return <PageLoader message="Loading KOT board..." minHeight={300} />;

  return (
    <>
      <CrmListPage
        title="Kitchen Order Tickets"
        subtitle="Track kitchen production from release through completion."
        hideSavedViews
        hideToolbar
        hideFooter
        headerActions={(
          <CrmTooltip content="Edit card properties">
            <CrmButton iconOnly size="small" variant="tertiary" onClick={() => setCardManagerOpen(true)} aria-label="Edit KOT card properties"><SettingsOutlinedIcon /></CrmButton>
          </CrmTooltip>
        )}
      >
        <div className="operations-board operations-board--kot operations-board--crm">
          <div className="operations-board__controls operations-board__controls--crm">
            <div className="operations-board__status-tabs" aria-label="KOT status">
              {[{ id: 'all', label: 'All' }, ...STATUS_OPTIONS.map((status) => ({ id: status, label: formatStatusLabel(status) }))].map((item) => (
                <button key={item.id} type="button" className={`operations-board__status-tab ${statusFilter === item.id ? 'is-active' : ''}`} onClick={() => setStatusFilter(item.id)}>
                  {item.label}<span className="operations-board__count">{statusCounts[item.id] || 0}</span>
                </button>
              ))}
            </div>
            <div className="operations-board__filters operations-board__filters--crm">
              <CrmSearchInput value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search KOT, customer, event…" aria-label="Search KOTs" />
              <CrmSelect label="Display" value={displayView} onChange={(event) => { const value = event.target.value; setDisplayView(value); try { localStorage.setItem('jdh:kots:display', value); } catch { /* keep current session choice */ } }}><option value="cards">Cards</option><option value="kds">KDS stages</option></CrmSelect>
              <CrmSelect label="Range" value={range} onChange={(event) => setRange(event.target.value)}>{RANGE_OPTIONS.map((option) => <option key={option} value={option}>{formatStatusLabel(option)}</option>)}</CrmSelect>
              <CrmSelect label="Order source" value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}><option value="all">All sources</option><option value="website">Website orders</option><option value="catering">Catering orders</option></CrmSelect>
              <CrmSelect label="Sort by" value={sortBy} onChange={(event) => setSortBy(event.target.value)}><option value="scheduled">Scheduled time</option><option value="status">Order status</option><option value="pax">PAX (high to low)</option></CrmSelect>
              {hasActiveFilters ? <CrmButton size="small" variant="tertiary" onClick={() => { setSearchQuery(''); setRange('upcoming'); setStatusFilter('all'); setSourceFilter('all'); setSortBy('scheduled'); }}>Clear</CrmButton> : null}
            </div>
          </div>

          <div className="operations-board__content">
            <div role="status" aria-live="polite">{loadError ? <p className="operations-board__error">{loadError} <CrmButton size="small" onClick={() => loadKots(range)}>Retry</CrmButton></p> : null}</div>
            <div className="operations-board__summary"><span>Showing <strong>{filteredAndSortedKots.length}</strong> of {kots.length} tickets</span><span>Auto-refreshes every 15 seconds</span></div>
            {loading ? <PageLoader message="Loading KOT board…" minHeight={300} /> : loadError && !kots.length ? null : filteredAndSortedKots.length === 0 ? (
              <div className="operations-board__empty"><div><strong>No KOTs found</strong><p>Try changing the status, range or filters.</p></div></div>
            ) : displayView === 'kds' ? (
              <DragDropContext onDragEnd={onDragEnd} onDragStart={({ draggableId }) => setDragTarget(nextKotAction(kots.find((kot) => String(kot.id) === draggableId))?.status || null)}>
                <div className="kot-kds">
                  {STATUS_OPTIONS.map((status) => <section key={status} className="kot-kds__column">
                    <h2>{formatStatusLabel(status)} <span>{filteredAndSortedKots.filter((kot) => kot.status === status).length}</span></h2>
                    <Droppable droppableId={status} isDropDisabled={status === 'pending' || status === 'cancelled' || Boolean(dragTarget && dragTarget !== status)}>{(provided, snapshot) => <div ref={provided.innerRef} {...provided.droppableProps} className={`kot-kds__lane ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                      {filteredAndSortedKots.filter((kot) => kot.status === status).map((kot, index) => <Draggable key={kot.id} draggableId={String(kot.id)} index={index} isDragDisabled={!nextKotAction(kot) || busyIds.has(kot.id) || completionDialog.open}>{(drag) => <div ref={drag.innerRef} {...drag.draggableProps} {...drag.dragHandleProps}>{renderKotCard(kot)}</div>}</Draggable>)}
                      {provided.placeholder}
                    </div>}</Droppable>
                  </section>)}
                </div>
              </DragDropContext>
            ) : (
              <div className="operations-board__grid">
                {filteredAndSortedKots.map(renderKotCard)}
              </div>
            )}
          </div>
        </div>
      </CrmListPage>

      <CrmModal
        open={completionDialog.open}
        onClose={completing ? undefined : () => setCompletionDialog({ open: false, kot: null, loading: false, workflow: null, deliveryDate: '', deliveryTime: '' })}
        title="Complete KOT and hand off?"
        subtitle="Ready means kitchen preparation is finished. Completing performs the configured handoff, including Delivery creation for delivery-mode Orders."
        size="md"
        footer={<><CrmButton variant="secondary" disabled={completing} onClick={() => setCompletionDialog({ open: false, kot: null, loading: false, workflow: null, deliveryDate: '', deliveryTime: '' })}>Cancel</CrmButton><CrmButton variant="primary" loading={completing} disabled={!completionValid} onClick={confirmComplete}>Complete KOT</CrmButton></>}
      >
        {completionDialog.error ? <p role="alert">{completionDialog.error}</p> : completionDialog.loading ? <p className="crm-secondary-text">Resolving next fulfilment step…</p> : <div className="operations-confirm-flow"><p><strong>{completionDialog.kot?.order_number || `KOT #${completionDialog.kot?.id || ''}`}</strong> will be marked completed.</p>{completionMode === 'DELIVERY' ? <><p>Completing this KOT creates the Delivery record. Storefront slot date/time is prefilled when available; operations can adjust it here before completion.</p><div className="settings-form-grid"><CrmInput label="Driver name" required value={completionDialog.driverName || ''} onChange={(event) => setCompletionDialog((current) => ({ ...current, driverName: event.target.value }))} /><CrmInput label="Driver phone" type="tel" required value={completionDialog.driverPhone || ''} onChange={(event) => setCompletionDialog((current) => ({ ...current, driverPhone: event.target.value }))} /><CrmInput label="Vehicle / identifier" value={completionDialog.driverVehicle || ''} onChange={(event) => setCompletionDialog((current) => ({ ...current, driverVehicle: event.target.value }))} /><CrmDateInput label="Delivery date" required value={completionDialog.deliveryDate} onChange={(value) => setCompletionDialog((current) => ({ ...current, deliveryDate: value }))} /><CrmTimeInput label="Delivery time" required value={completionDialog.deliveryTime} onChange={(event) => setCompletionDialog((current) => ({ ...current, deliveryTime: event.target.value }))} /></div></> : <div className="operations-confirm-flow__next"><span>Next operational step</span><strong>{completionMode === 'PICKUP' ? 'Pickup workflow continues according to the fulfilment profile.' : 'The configured fulfilment profile controls the next transition.'}</strong></div>}</div>}
      </CrmModal>

      <CrmColumnManager
        open={cardManagerOpen}
        onClose={() => setCardManagerOpen(false)}
        columns={KOT_CARD_FIELDS}
        preferences={cardPreferences}
        onApply={applyCardPreferences}
        onRestoreDefaults={() => applyCardPreferences(defaultCardPreferences())}
        title="Edit card properties"
        subtitle="Choose which KOT details appear on every card and set their order."
      />
      <NotificationSnackbar open={notif.open} message={notif.message} severity={notif.severity} onClose={() => setNotif((previous) => ({ ...previous, open: false }))} />
    </>
  );
}

export default KOTBoard;
